import { Box } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import DataTable from "../components/DataTable";
import Subheader from "../components/Subheader";
import Api from "../openapi";
import {
  DriverListResponse,
  PagedResponseDriverListResponse,
  PagedResponsePartnerUserListResponse,
} from "../openapi/client";
import { GridPaginationModel, GridCallbackDetails, GridSortModel, GridRenderCellParams } from "@mui/x-data-grid";
import { generateRandom } from "../utils";
import useConfirm from "../hooks/useConfirm";
import { DeleteLabelIcon } from "../components/IconLabel";
import { useDispatch } from "react-redux";
import { setNotification } from "../store/notificationSlice";
import DriverCrud from "../components/DriverCrud";

interface IEditDriver {
  show: boolean;
  driver?: DriverListResponse;
}

export default function Drivers() {
  const dispatch = useDispatch();

  const [drivers, setDrivers] = useState<PagedResponseDriverListResponse[]>([]);
  const [createShow, setCreateShow] = useState(false);
  const [editShow, setEditShow] = useState<IEditDriver>({ show: false, driver: undefined });
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [retriggerReq, setRetriggerReq] = useState<number>();

  const [ConfirmDialog, confirmDelete] = useConfirm({
    titleIcon: <DeleteLabelIcon />,
    text: "You are about to permanently delete a driver from the list, and this action is irreversible. Are you sure you want to proceed?",
  });

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: "fullName",
      sort: "asc",
    },
  ]);

  const handlePaginationChange = (model: GridPaginationModel, details: GridCallbackDetails<any>) => {
    setPaginationModel(model);
  };
  const handleSortChange = (sortModel: GridSortModel) => {
    sortModel.length && setSortModel(sortModel);
  };

  const create = () => {
    setCreateShow(true);
  };

  const deleteRec = async (params: any) => {
    const answer = await confirmDelete();
    if (!answer) return;

    try {
      const req = await Api.Driver.deleteDriver(params.row.id);
      dispatch(
        setNotification({
          show: true,
          severity: "success",
          message: "Driver has been deleted successfully",
        })
      );
      setRetriggerReq(generateRandom());
    } catch (error: any) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: error.response?.data?.message || error.message,
        })
      );
    }
  };

  const edit = (params: any) => {
    setEditShow({ show: true, driver: params.row });
  };

  const handleClose = () => {
    setCreateShow(false);
    setEditShow({ show: false, driver: undefined });
  };
  const handleConfirm = () => {
    setRetriggerReq(generateRandom());
  };

  const getStatus = (params: GridRenderCellParams<any, any>) => {
    const userActive = Boolean(params.row.active);
    const subscriptionStatus = userActive ? "Active" : "Inactive";
    const subscriptionClass = userActive ? "active" : "passive";
    return <Box className={`status-label ${subscriptionClass}`}>{subscriptionStatus}</Box>;
  };

  const columns = useMemo(() => {
    return [
      {
        field: "fullName",
        minWidth: 250,
        flex: 2,
        headerName: "Name",
        // renderCell: (params: any) => getUserNameWithIcon(params.row.fullName, params.row.channel),
      },
      {
        field: "phoneNumber",
        minWidth: 120,
        flex: 2,
        headerName: "Phone Number",
        // valueGetter: (params: any) => params.row.phoneNumber,
      },
      // {
      //   field: "order",
      //   minWidth: 120,
      //   flex: 2,
      //   headerName: "Order",
      //   // valueGetter: (params: any) => params.row.phoneNumber,
      // },
      {
        field: "active",
        minWidth: 80,
        flex: 1,
        headerName: "Status",
        renderCell: getStatus,
      },
    ];
  }, [drivers]);

  const getDrivers = async () => {
    setLoading(true);
    try {
      const res = await Api.Driver.listDriver(
        paginationModel.page,
        paginationModel.pageSize,
        sortModel.map((i) => `${i.field},${i.sort}`)
      );
      setDrivers(res.data.content as PagedResponsePartnerUserListResponse[]);
      setTotalRowCount(Number(res.data.totalElements));
    } catch (error) {}
    setLoading(false);
  };

  useEffect(() => {
    getDrivers();
  }, [paginationModel, sortModel, retriggerReq]);

  return (
    <Box className="users">
      <Subheader page="drivers" />
      <Box p={6} className="users-container">
        <DataTable
          rows={drivers}
          rowCount={totalRowCount}
          columns={columns}
          sortingMode="server"
          sortModel={sortModel}
          paginationMode="server"
          paginationModel={paginationModel}
          paginationChange={handlePaginationChange}
          sortChange={handleSortChange}
          loading={loading}
          create={create}
          edit={edit}
          deleteRec={deleteRec}
          options={{
            entityName: "driver",
            permission: { create: "DRIVER_CREATE", edit: "DRIVER_EDIT", delete: "DRIVER_DELETE" },
          }}
        />
      </Box>
      <DriverCrud create open={createShow} title="Create New Driver" onClose={handleClose} onConfirm={handleConfirm} />
      <DriverCrud
        edit
        open={editShow.show}
        driver={editShow.driver}
        title="Edit Driver"
        onClose={handleClose}
        onConfirm={handleConfirm}
      />
      <ConfirmDialog />
    </Box>
  );
}
